import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { AuthService } from '../../core/tenant/auth.service';
import { FeatureService } from '../../core/tenant/feature.service';

export interface UsuarioDelNegocio { email: string; role: string; status: string; nombre?: string | null }

const ROLES: Readonly<Record<string, string>> = { admin: 'Administrador', cajero: 'Cajero', vendedor: 'Vendedor', bodeguero: 'Bodeguero' };

/**
 * «Usuarios del negocio» (B5, Producto): la lista en una tabla —Nombre, Correo, Rol, Estado— y debajo «Nuevo usuario».
 * Antes era una lista con viñetas dentro de un Swal, y `status` no se pintaba: un usuario suspendido parecía activo.
 *
 * `<dialog>` nativo: Swal no apila (un aviso encima cerraría el formulario). Los avisos van dentro del diálogo, porque un
 * Swal quedaría DEBAJO de la capa superior. Corta la propagación de sus teclas: la venta rápida escucha `document:keydown`
 * y un Esc o una F no deben llegarle mientras se escribe aquí. Sin zone.js: todo el estado es de signals.
 */
@Component({
  selector: 'app-usuarios-del-negocio',
  standalone: true,
  template: `
    <dialog #dialogo class="usuarios-dialogo" (keydown)="$event.stopPropagation()" aria-labelledby="usuarios-titulo">
      <h2 id="usuarios-titulo">Usuarios del negocio</h2>
      @if (errorDeCarga()) {
        <p class="usuarios-error" role="alert">No se pudo cargar. {{ errorDeCarga() }}</p>
      } @else if (usuarios().length === 0) {
        <p class="usuarios-vacio" data-testid="usuarios-vacio">Todavía no hay más usuarios.</p>
      } @else {
        <table class="usuarios-tabla" data-testid="usuarios-tabla">
          <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Estado</th></tr></thead>
          <tbody>
            @for (u of usuarios(); track u.email) {
              <tr>
                <td>{{ u.nombre || '—' }}</td>
                <td>{{ u.email }}</td>
                <td>{{ rol(u.role) }}</td>
                <td><span class="usuarios-estado" [class.inactivo]="!activo(u)">{{ activo(u) ? 'Activo' : 'Inactivo' }}</span></td>
              </tr>
            }
          </tbody>
        </table>
      }

      <h3>Nuevo usuario</h3>
      <form class="usuarios-form" (submit)="$event.preventDefault(); crear()">
        <input id="u-email" type="email" placeholder="Email del nuevo usuario" autocomplete="off">
        <input id="u-pass" type="password" placeholder="Contraseña (mín. 6)" autocomplete="new-password">
        <select id="u-role" (change)="rolElegido.set($any($event.target).value)">
          <option value="cajero">Cajero</option><option value="admin">Administrador</option>
          @if (conVendedores()) { <option value="vendedor">Vendedor</option> }
          @if (conBodegueros()) { <option value="bodeguero">Bodeguero</option> }
        </select>
        <!-- W1: «No ve precios, no cobra…» espera RolBodegueroTest en verde (TEXTOS, Bodega W1); hasta entonces, solo esto. -->
        @if (rolElegido() === 'bodeguero') { <p class="usuarios-ayuda" data-testid="usuarios-ayuda-bodeguero">Entra directo al Modo Bodega.</p> }
        @if (conVendedores()) { <input id="u-nombre" type="text" placeholder="Nombre que se ve en la caja (p. ej. Ana R.)"> }
        @if (aviso()) { <p class="usuarios-aviso" [class.ok]="avisoOk()" role="status" data-testid="usuarios-aviso">{{ aviso() }}</p> }
        <div class="usuarios-acciones">
          <button type="button" class="usuarios-secundario" (click)="cerrar()">Cerrar</button>
          <button type="submit" class="usuarios-primario" [disabled]="creando()">Crear usuario</button>
        </div>
      </form>
    </dialog>`,
  styles: [`
    .usuarios-dialogo { width: min(640px, calc(100vw - 32px)); border: none; border-radius: var(--radio-lg); padding: var(--esp-5);
      background: var(--color-card-light); color: var(--color-slate-800); }
    .usuarios-dialogo::backdrop { background: rgba(15, 23, 42, 0.5); }
    .usuarios-dialogo h2 { margin: 0 0 var(--esp-3); font-size: var(--texto-lg); }
    .usuarios-dialogo h3 { margin: var(--esp-4) 0 var(--esp-2); font-size: var(--texto-md); }
    .usuarios-tabla { width: 100%; border-collapse: collapse; font-size: var(--texto-sm); }
    .usuarios-tabla th { text-align: left; color: var(--color-slate-500); font-weight: 600; border-bottom: 1px solid var(--color-slate-200); padding: var(--esp-1) var(--esp-2); }
    .usuarios-tabla td { border-bottom: 1px solid var(--color-slate-100); padding: var(--esp-2); overflow-wrap: anywhere; }
    .usuarios-estado { padding: 0 var(--esp-2); border-radius: var(--radio-pastilla); background: var(--tint-ok); color: var(--estado-ok-texto); font-size: var(--texto-xs); }
    .usuarios-estado.inactivo { background: var(--color-slate-100); color: var(--color-slate-600); }
    .usuarios-vacio { color: var(--color-slate-500); font-size: var(--texto-sm); }
    .usuarios-error { color: var(--estado-error-texto); font-size: var(--texto-sm); }
    .usuarios-form { display: grid; gap: var(--esp-2); }
    .usuarios-form input, .usuarios-form select { padding: var(--esp-2); border: 1px solid var(--color-slate-300); border-radius: var(--radio-sm); font-size: var(--texto-sm); }
    .usuarios-ayuda { margin: 0; color: var(--color-slate-600); font-size: var(--texto-sm); }
    .usuarios-aviso { margin: 0; color: var(--estado-error-texto); font-size: var(--texto-sm); }
    .usuarios-aviso.ok { color: var(--estado-ok-texto); }
    .usuarios-acciones { display: flex; justify-content: flex-end; gap: var(--esp-2); }
    .usuarios-primario { background: var(--color-primary); color: #fff; border: none; border-radius: var(--radio-md); padding: var(--esp-2) var(--esp-4); cursor: pointer; }
    .usuarios-secundario { background: transparent; border: 1px solid var(--color-slate-300); border-radius: var(--radio-md); padding: var(--esp-2) var(--esp-4); cursor: pointer; }
  `],
})
export class UsuariosDelNegocioComponent {
  private readonly auth = inject(AuthService);
  private readonly features = inject(FeatureService);
  private readonly dialogo = viewChild.required<ElementRef<HTMLDialogElement>>('dialogo');

  readonly usuarios = signal<UsuarioDelNegocio[]>([]);
  readonly errorDeCarga = signal<string | null>(null);
  readonly aviso = signal<string | null>(null);
  readonly avisoOk = signal(false);
  readonly creando = signal(false);
  readonly conVendedores = signal(false);
  /** W1: el rol solo se ofrece con el módulo `bodega`: sin él, -mt no lo conoce (400 «Rol inválido»). */
  readonly conBodegueros = signal(false);
  readonly rolElegido = signal('cajero');

  rol(r: string): string { return ROLES[r] ?? r; }
  /** -mt V4: `active | suspended`, y solo `active` entra (V39). Cualquier otro valor es Inactivo. */
  activo(u: UsuarioDelNegocio): boolean { return (u.status ?? '').toLowerCase() === 'active'; }

  async abrir(): Promise<void> {
    this.conVendedores.set(this.features.has('mayorista'));
    this.conBodegueros.set(this.features.has('bodega'));
    this.aviso.set(null);
    await this.cargar();
    const d = this.dialogo().nativeElement;
    if (!d.open) d.showModal();
  }

  cerrar(): void { this.dialogo().nativeElement.close(); }

  private async cargar(): Promise<void> {
    try {
      this.usuarios.set(await this.auth.getUsers());
      this.errorDeCarga.set(null);
    } catch (e) {
      this.errorDeCarga.set(e instanceof Error ? e.message : '');
    }
  }

  private campo(id: string): HTMLInputElement | null {
    return this.dialogo().nativeElement.querySelector<HTMLInputElement>('#' + id);
  }

  async crear(): Promise<void> {
    if (this.creando()) return; // dos submit seguidos (Enter + clic) no crean dos veces (DrDev002)
    const val = (id: string) => this.campo(id)?.value.trim() ?? '';
    const email = val('u-email'), pass = val('u-pass'), role = val('u-role'), nombre = val('u-nombre');
    const falta = !email || !pass ? 'Email y contraseña requeridos'
      : pass.length < 6 ? 'La contraseña debe tener al menos 6 caracteres'
      : role === 'vendedor' && !nombre ? 'Escribe el nombre del vendedor: es el que se elige en la caja'
      : null;
    this.avisoOk.set(false);
    if (falta) { this.aviso.set(falta); return; }
    this.creando.set(true);
    try {
      await this.auth.createUser(email, pass, role as 'admin' | 'cajero' | 'vendedor' | 'bodeguero', nombre);
      for (const id of ['u-email', 'u-pass', 'u-nombre']) { const c = this.campo(id); if (c) c.value = ''; }
      this.avisoOk.set(true);
      this.aviso.set(role === 'vendedor' ? 'Vendedor creado' : 'Usuario creado');
      await this.cargar();
    } catch (e) {
      this.aviso.set(`No se pudo crear. ${e instanceof Error ? e.message : ''}`.trim());
    } finally {
      this.creando.set(false);
    }
  }
}
